<template>
    <v-row style=" font-family: Arial, Helvetica, sans-serif; height: 300px; background-color: black; color: white; font-size: 1.7rem;" class="active-players-row rounded-xl px-0 mx-0 ml-1" align="center" justify="center">
        <v-col align="center" class="active-players-logo py-0 my-0 px-0 mx-0">
            <img
                :src="`${assetsUrl}/${displayedTeam?.logo}`"
                width="150px"
                max-height="auto"
                contain
            >
        </v-col>

        <!-- players for the currently visible team -->
        <v-col v-for="player in displayedPlayers" :key="player.id" align="center" class="active-players-player px-1 py-0 my-0">
            <v-row class="my-0 mx-0 px-0 rounded-t-xl" style="background-color: black;" align="center" justify="center">
                <v-col cols="12">
                    <img
                        :src="`${assetsUrl}/${player.image}`"
                        width="auto"
                        height="100px"
                        contain
                        @load="fitNames"
                        @error="$event.target.src = defaultPlayerImg"
                    >
                    <div ref="names" class="active-players-name">
                        #{{ player.jersey }} {{ player.surname }}
                    </div>
                </v-col>
                <v-col cols="12" align="center" class="font-weight-bold px-0">
                    <span> {{ player.stats[0]?.points ?? 0 }} </span>
                    <span class="custom-orange-text">{{ 'points' }}</span>
                    <div cols="12" align="center" class="px-0 py-0 my-0" style="font-size: 1.2rem;">
                        <span class="custom-orange-text font-weight-bold mr-1">fouls</span>
                        <span v-for="i in foulsCount(player)" :key="`i-${i}`">
                            <v-icon :color="foulsCount(player) >= 5 ? 'red' : 'orange'">mdi-minus-thick</v-icon>
                        </span>
                        <span v-for="j in remainingFouls(player)" :key="`j-${j}`">
                            <v-icon color="white">mdi-minus-thick</v-icon>
                        </span>
                    </div>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
    import matchService from '@/services/match'
    import { foulsOf } from '@/utils/playerStats'
    export default {
        props: {
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                match: null,
                activePlayers: {
                    team1: [],
                    team2: []
                },
                // rotation state
                visibleTeamIndex: 1, // 1 => team1, 2 => team2
                activeTimer: null,
                rotateIntervalMs: 10000,
                rotationStage: 0 // renamed from _rotationStage
            }
        },
        computed: {
            displayedPlayers() {
                return this.visibleTeamIndex === 1 ? this.activePlayers.team1 : this.activePlayers.team2
            },
            displayedTeam() {
                return this.visibleTeamIndex === 1 ? this.match?.team1 : this.match?.team2
            }
        },
        watch: {
            displayedPlayers() {
                this.$nextTick(this.fitNames)
            }
        },
        mounted() {
            this.fetchMatch(this.$route.params.id).then((resp) => {
                this.match = resp
                this.getActivePlayers().then(() => {
                    this.startRotation()
                })
            })
        },
        beforeDestroy() {
            this.stopRotation()
        },
        methods: {
            fetchMatch(matchId) {
                return matchService.getMatch(matchId).then(resp => {
                    return resp.data
                })
            },
            async getActivePlayers() {
                try {
                    const resp = await matchService.getActivePlayers(this.$route.params.id)
                    // reset arrays to avoid duplicates on repeated calls
                    this.activePlayers.team1 = []
                    this.activePlayers.team2 = []

                    resp.data.team1Players.forEach(playerRef => {
                        const p = this.match.team1.players.find(p => p.id === playerRef.playerId)
                        if (p) this.activePlayers.team1.push(p)
                    })
                    resp.data.team2Players.forEach(playerRef => {
                        const p = this.match.team2.players.find(p => p.id === playerRef.playerId)
                        if (p) this.activePlayers.team2.push(p)
                    })
                } catch (e) {
                    console.error('Failed to load active players', e)
                }
            },
            startRotation() {
                this.stopRotation()
                this.rotationStage = 0
                // show team1 first
                this.visibleTeamIndex = 1
                this.rotationStage = 1

                // after rotateIntervalMs show team2
                this.activeTimer = setTimeout(() => {
                    this.visibleTeamIndex = 2
                    this.rotationStage = 2

                    // after another rotateIntervalMs emit and stop
                    this.activeTimer = setTimeout(() => {
                        // finished showing both teams once
                        this.$emit('setShowScreen')
                        this.stopRotation()
                    }, this.rotateIntervalMs)
                }, this.rotateIntervalMs)
            },
            // The card grows up to the screen edge; past that, shrink the names until they fit instead of cutting them
            fitNames() {
                const names = this.$refs.names || []
                const maxSize = 1.7
                const minSize = 1
                let size = maxSize
                this.$el.style.setProperty('--name-font-size', `${size}rem`)
                this.$el.style.setProperty('--name-white-space', 'nowrap')
                while (size > minSize && names.some(el => el.scrollWidth > el.clientWidth)) {
                    size = Math.max(minSize, size - 0.1)
                    this.$el.style.setProperty('--name-font-size', `${size}rem`)
                }
                // still too long at the smallest size: two lines rather than a cut name
                if (names.some(el => el.scrollWidth > el.clientWidth)) {
                    this.$el.style.setProperty('--name-white-space', 'normal')
                }
            },
            stopRotation() {
                if (this.activeTimer) {
                    clearTimeout(this.activeTimer)
                    this.activeTimer = null
                }
            },
            foulsCount(player) {
                // every foul of the player: personal, technical and unsportsmanlike
                const total = foulsOf(player?.stats?.[0] || {})
                return Math.min(5, Math.max(0, total))
            },
            remainingFouls(player) {
                const rem = 5 - this.foulsCount(player)
                return rem > 0 ? rem : 0
            }
        }
    }
</script>

<style>
    .v-progress-circular__info {
        display: block;
    }

    .icon-outline {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px  1px 0 #000,
            1px  1px 0 #000;
    }

    .circular-border {
        display: inline-flex;
        border: 2px solid #fff;     /* white border */
        border-radius: 50%;         /* make it circular */
        padding: 2px;               /* space between border and ring */
    }

    .player-line {
        display: flex;
        align-items: center;
    }
</style>

<style scoped>
    /* Columns keep a minimum width so the card lays out the same with 1–5 starters,
       and grow with long surnames (capped by the screen edge in LiveData) */
    .active-players-row {
        flex-wrap: nowrap;
        padding: 0 12px !important;
        width: max-content;
        min-width: 650px;
        max-width: 100%;
    }

    .active-players-logo {
        flex: 0 0 170px;
        max-width: 170px;
    }

    .active-players-player {
        flex: 0 1 auto;
        min-width: 150px;
        max-width: none;
    }

    .active-players-name {
        font-size: var(--name-font-size, 1.7rem);
        line-height: 1.15;
        white-space: var(--name-white-space, nowrap);
        overflow-wrap: anywhere;
    }
</style>
